// We disable no-param-reassign because we need to mutate the object
/* eslint-disable no-param-reassign */

import {
  EProgram,
  EQuestionTypes,
  ETodayDescriptionChoices,
  TAnswers,
  TSurveyElement,
  ISurveyPageData,
  ISurveyRequestData,
  ISurveys,
  SURVEY_TYPES,
  SurveyTimesAnswered,
  TimesAnswered,
  SurveyPersonalizedName,
} from '../../types/surveys';

import { OnboardingAnswers, ParsedSurvey } from '../../types/onboarding/survey';
import { DueDateRecordEvent, Trimester } from '../../services/DataPipeline/models';

export const decodeHtmlEntities = (str: string): string => {
  return str.replace(/&#(\d+);/g, (match, dec) => {
    return String.fromCharCode(dec);
  });
};

export const getNamePrevBtn = (): string => `${decodeHtmlEntities('&#5176;')} Previous Question`;

export const prepareSurveyQuestionsStructure = (surveys: ISurveys, answers: OnboardingAnswers) => {
  const survey: ParsedSurvey = JSON.parse(surveys[0].surveyJson);

  const surveyQuestions: Record<
    string,
    { possible_answers: string[] | boolean[] | null; answer: string }
  > = {};
  const titleToNameMapper: Record<string, string[]> = {};

  const getPossibleAnswers = (elem: TSurveyElement) => {
    switch (elem.type) {
      case 'radiogroup': {
        return elem.choices?.map(x => x.value);
      }
      case 'boolean': {
        return [true, false];
      }
      case 'matrix': {
        return elem.columns?.map(x => x.value);
      }
      default: {
        return null;
      }
    }
  };

  survey.pages.forEach(page => {
    page.elements.forEach(elem => {
      if (elem.name in answers) {
        if (elem.type === 'matrix') {
          const possibleAnswers = getPossibleAnswers(elem);
          elem.rows?.forEach(row => {
            const answer = answers[elem.name as EQuestionTypes];
            const answerValue =
              typeof answer === 'object' && !Array.isArray(answer) ? answer[row.value] : '';

            surveyQuestions[row.text] = {
              possible_answers: possibleAnswers ?? null,
              answer: answerValue ? String(answerValue) : '',
            };
            titleToNameMapper[row.text] = [elem.name, row.value];
          });
        } else {
          const answer = answers[elem.name as EQuestionTypes];
          // For non-matrix types, the answer is the direct value (string, boolean, etc.)
          // Only for nested objects do we need to access by key
          const answerValue =
            typeof answer === 'object' && !Array.isArray(answer) ? answer[elem.name] : answer;

          surveyQuestions[elem.title] = {
            possible_answers: getPossibleAnswers(elem) ?? null,
            answer: answerValue !== undefined && answerValue !== null ? String(answerValue) : '',
          };
          titleToNameMapper[elem.title] = [elem.name];
        }
      }
    });
  });
  return { surveyQuestions, titleToNameMapper };
};

export const incrementTimesAnswered = (
  surveyType: SURVEY_TYPES,
  timesAnswered: TimesAnswered,
  answers: OnboardingAnswers,
): SurveyTimesAnswered => {
  // Any is required because the object can be nested and recursive
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const increment = (obj: any, result: any) => {
    Object.keys(obj).forEach(key => {
      if (typeof obj[key] === 'object') {
        result[key] = result[key] || {};
        increment(obj[key], result[key]);
      } else {
        result[key] = (result[key] || 0) + 1;
      }
    });
  };
  const surveyTimesAnswered: SurveyTimesAnswered = timesAnswered[surveyType] || {};
  increment(answers, surveyTimesAnswered);
  return surveyTimesAnswered;
};

export const getValueByNestedKeys = (
  obj: Record<string, string | number>,
  keys: Array<string>,
): string | undefined => {
  let current = '';

  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (
      current !== null &&
      typeof current === 'object' &&
      Object.prototype.hasOwnProperty.call(current, key)
    ) {
      current = current ? current[key] : '';
    } else {
      return undefined;
    }
  }

  return typeof current === 'string' ? current : undefined;
};

// Program name mapping utility
export const getProgramName = (programId: string): string => {
  switch (programId) {
    case '40731':
      return 'prepare';
    case '40732':
      return 'reclaim';
    case '40733':
      return 'surpass';
    default:
      return '';
  }
};

export const getProgramDescription = (description: string) => {
  const replaced = description
    .replace('<em>', '')
    .replace('</em>', '')
    .replace(/(\r\n)<strong>(.*)<\/strong>(\r\n)/gm, '');

  return replaced;
};

export const getProgram = (answers: TAnswers<EQuestionTypes>) => {
  const surpassArray: ETodayDescriptionChoices[] = [
    ETodayDescriptionChoices.more_6,
    ETodayDescriptionChoices.planning,
    ETodayDescriptionChoices.other,
  ];

  if (answers[EQuestionTypes.todayDescription] === ETodayDescriptionChoices.pregnant) {
    return EProgram.prepare;
  }

  if (
    surpassArray.includes(answers[EQuestionTypes.todayDescription] as ETodayDescriptionChoices) &&
    !answers[EQuestionTypes.drCheck] &&
    (answers[EQuestionTypes.opf] ||
      (!answers[EQuestionTypes.opf] && !answers[EQuestionTypes.prolapse]))
  ) {
    return EProgram.surpass;
  }
  return EProgram.reclaim;
};

// Survey name determination based on page data
export const getPersonalizationSurveyName = (pageData: ISurveyPageData): string => {
  if (pageData.isReactivated) {
    return SurveyPersonalizedName.REACTIVATION_ONBOARDING;
  }

  if (!pageData.path) {
    return SurveyPersonalizedName.NEW_ONBOARDING;
  }

  if (pageData.pathStatus?.fully_complete) {
    return SurveyPersonalizedName.END_OF_PATH;
  }

  return SurveyPersonalizedName.CHANGE_STAGE;
};

// Survey data for request
export const getSurveyDataForRequest = (
  surveyTrigger: string,
  pageData?: ISurveyPageData,
): ISurveyRequestData => {
  if (surveyTrigger === 'onboarding') {
    return { type: SURVEY_TYPES.ONBOARDING, version: pageData?.onboardingSurveyVersion || 1 };
  }
  return { type: SURVEY_TYPES.CHANGE_STAGE, version: pageData?.changeStageSurveyVersion || 1 };
};

export const areObjectsEqual = (
  object1: Record<string, string | boolean | null>,
  object2: Record<string, string | boolean | null>,
) => {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  return keys1.every(key => object1[key] === object2[key]);
};

const TOTAL_PREGNANCY_DAYS = 280;
const MS_IN_DAY = 1000 * 60 * 60 * 24;
const FIRST_TRIMESTER_END = 91;
const SECOND_TRIMESTER_START = 92;
const SECOND_TRIMESTER_END = 182;
const THIRD_TRIMESTER_START = 183;
const THIRD_TRIMESTER_END = 280;

const calculateTrimester = (pregnancyDays: number): Trimester => {
  if (pregnancyDays > 0 && pregnancyDays <= FIRST_TRIMESTER_END) {
    return Trimester.FIRST_TRIMESTER;
  }
  if (pregnancyDays >= SECOND_TRIMESTER_START && pregnancyDays <= SECOND_TRIMESTER_END) {
    return Trimester.SECOND_TRIMESTER;
  }
  if (pregnancyDays >= THIRD_TRIMESTER_START && pregnancyDays <= THIRD_TRIMESTER_END) {
    return Trimester.THIRD_TRIMESTER;
  }
  return Trimester.UNKNOWN;
};

export const calculateTrackDueDateRecordedData = (dueDate: string): DueDateRecordEvent => {
  const dueDateObj = new Date(dueDate);
  // Normalize dates to midnight to avoid time component issues
  dueDateObj.setHours(0, 0, 0, 0);
  const currentDate = new Date();
  // Normalize dates to midnight to avoid time component issues
  currentDate.setHours(0, 0, 0, 0);
  const daysUntilDueDate = Math.ceil((dueDateObj.getTime() - currentDate.getTime()) / MS_IN_DAY); // Convert milliseconds to days
  return {
    due_date: dueDateObj.toISOString(),
    days_until_due_date: daysUntilDueDate,
    trimester: calculateTrimester(TOTAL_PREGNANCY_DAYS - daysUntilDueDate),
  };
};
